import React, { useState, useEffect } from 'react';
import { io } from 'socket.io-client';
import { Link } from 'react-router-dom';
import axios from '../api/axios';
import { BACKEND_URL } from '../config';
import './Overview.css';

const socket = io(BACKEND_URL);

function Overview() {
  const [services, setServices] = useState({});
  const [obsConnected, setObsConnected] = useState(false);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [systemInfo, setSystemInfo] = useState(null);
  const [viewerStats, setViewerStats] = useState([]);
  const [youtubeKeys, setYoutubeKeys] = useState({ keys: [], currentIndex: 0 });

  useEffect(() => {
    // Fetch system info
    const fetchSystemInfo = async () => {
      try {
        const { data } = await axios.get('/api/system/info');
        setSystemInfo(data);
      } catch (error) {
        console.error('Error fetching system info:', error);
      }
    };

    // Fetch viewer stats
    const fetchViewerStats = async () => {
      try {
        const { data } = await axios.get('/api/system/viewers');
        setViewerStats(data);
      } catch (error) {
        console.error('Error fetching viewer stats:', error);
      }
    };

    // Fetch YouTube API key status
    const fetchYoutubeKeys = async () => {
      try {
        const { data } = await axios.get('/api/system/youtube-keys');
        setYoutubeKeys(data);
      } catch (error) {
        console.error('Error fetching YouTube keys:', error);
      }
    };

    fetchSystemInfo();
    fetchViewerStats();
    fetchYoutubeKeys();
    
    const systemInterval = setInterval(fetchSystemInfo, 2000);
    const viewerInterval = setInterval(fetchViewerStats, 10000); // Every 10 seconds
    const keysInterval = setInterval(fetchYoutubeKeys, 5000); // Every 5 seconds

    return () => clearInterval(systemInterval);
  }, []);

  useEffect(() => {
    console.log('[Overview] Setting up socket listeners');
    
    socket.on('services:status', (status) => {
      console.log('[Overview] Received services status:', Object.keys(status));
      setServices(status);
      setLoading(false);
    });

    socket.on('obs:connected', () => {
      setObsConnected(true);
    });

    socket.on('obs:disconnected', () => {
      setObsConnected(false);
    });

    socket.on('obs:status', (data) => {
      setStats(data);
    });

    // Request status after a short delay
    const timer = setTimeout(() => {
      console.log('[Overview] Requesting services status');
      socket.emit('services:getStatus');
      socket.emit('obs:connect');
    }, 300);

    // Fallback: stop loading after 3 seconds
    const fallbackTimer = setTimeout(() => {
      console.log('[Overview] Fallback: stopping loading');
      setLoading(false);
    }, 3000);

    const interval = setInterval(() => {
      if (obsConnected) {
        socket.emit('obs:getStatus');
      }
    }, 5000);

    return () => {
      clearTimeout(timer);
      clearTimeout(fallbackTimer);
      clearInterval(interval);
      socket.off('services:status');
      socket.off('obs:connected');
      socket.off('obs:disconnected');
      socket.off('obs:status');
    };
  }, [obsConnected]);

  const getRunningCount = () => {
    return Object.values(services).filter(s => s.status === 'running').length;
  };

  const getTotalUptime = () => {
    const total = Object.values(services).reduce((sum, s) => sum + (s.uptime || 0), 0);
    return formatTime(total / 1000);
  };

  return (
    <div className="overview">
      <div className="page-header">
        <h2>Übersicht</h2>
        <p>Willkommen im Stream Control Center</p>
      </div>

      <div className="overview-grid">
        {loading ? (
          <div className="loading-state wide">
            <div className="spinner"></div>
            <p>Daten werden geladen...</p>
          </div>
        ) : (
          <>
            {/* Quick Stats */}
            <div className="overview-card">
              <h3>Services</h3>
          <div className="stat-big">{getRunningCount()} / {Object.keys(services).length}</div>
          <p>Aktive Services</p>
          <Link to="/dashboard" className="card-link">Verwalten →</Link>
        </div>

        <div className="overview-card">
          <h3>OBS Status</h3>
          <div className="stat-big">{obsConnected ? '✓' : '✕'}</div>
          <p>{obsConnected ? 'Verbunden' : 'Nicht verbunden'}</p>
          <Link to="/obs" className="card-link">OBS Control →</Link>
        </div>

        <div className="overview-card">
          <h3>Stream Status</h3>
          <div className="stat-big">{stats.streaming ? '🔴 LIVE' : '⚫ Offline'}</div>
          <p>{stats.streaming ? `${formatTime(stats.stats?.streamTime || 0)}` : 'Nicht gestreamt'}</p>
          <Link to="/obs" className="card-link">Stream starten →</Link>
        </div>

        <div className="overview-card">
          <h3>Commands</h3>
          <div className="stat-big">!</div>
          <p>Custom Commands verwalten</p>
          <Link to="/commands" className="card-link">Commands →</Link>
        </div>

        {/* Service Status List */}
        <div className="overview-card wide">
          <h3>Service Status</h3>
          <div className="service-list">
            {Object.entries(services).map(([name, service]) => (
              <div key={name} className="service-row">
                <span className={`status-dot ${service.status}`}></span>
                <span className="service-name">{name.replace('-', ' ').toUpperCase()}</span>
                <div className={`service-pulse ${service.status === 'running' ? 'running-pulse' : ''}`}>
                  <div className="pulse-bar"></div>
                  <div className="pulse-bar"></div>
                  <div className="pulse-bar"></div>
                  <div className="pulse-bar"></div>
                  <div className="pulse-bar"></div>
                </div>
                <span className={`service-status ${service.status}`}>{service.status}</span>
                {service.uptime > 0 && (
                  <span className="service-uptime">{formatTime(service.uptime / 1000)}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="overview-card wide">
          <h3>Quick Actions</h3>
          <div className="quick-actions">
            <Link to="/dashboard" className="action-btn">
              <span className="action-icon">🎮</span>
              <span>Services verwalten</span>
            </Link>
            <Link to="/obs" className="action-btn">
              <span className="action-icon">📹</span>
              <span>OBS steuern</span>
            </Link>
            <Link to="/commands" className="action-btn">
              <span className="action-icon">💬</span>
              <span>Commands bearbeiten</span>
            </Link>
            <Link to="/settings/twitch-main" className="action-btn">
              <span className="action-icon">⚙</span>
              <span>Einstellungen</span>
            </Link>
          </div>
        </div>

        {/* System Monitoring */}
        {systemInfo && (
          <>
            <div className="overview-card system-card">
              <h3>CPU | {systemInfo.cpu.brand}</h3>
              <div className="system-stat">
                <div className="stat-label">Auslastung</div>
                <div className="stat-value">{systemInfo.cpu.usage}%</div>
                <div className="progress-bar">
                  <div className="progress-fill cpu" style={{ width: `${systemInfo.cpu.usage}%` }}></div>
                </div>
              </div>
              <div className="system-details">
                <span>{systemInfo.cpu.cores} Kerne ({systemInfo.cpu.physicalCores} physisch)</span>
                <span>{systemInfo.cpu.speed} GHz</span>
              </div>
            </div>

            <div className="overview-card system-card">
              <h3>RAM | {formatBytes(systemInfo.memory.total)}</h3>
              <div className="system-stat">
                <div className="stat-label">Verwendet</div>
                <div className="stat-value">{formatBytes(systemInfo.memory.used)} / {formatBytes(systemInfo.memory.total)}</div>
                <div className="progress-bar">
                  <div className="progress-fill ram" style={{ width: `${systemInfo.memory.usagePercent}%` }}></div>
                </div>
              </div>
              <div className="system-details">
                <span>{systemInfo.memory.usagePercent}% Auslastung</span>
                <span>{formatBytes(systemInfo.memory.free)} frei</span>
              </div>
            </div>

            {systemInfo.gpu && systemInfo.gpu.length > 0 && (
              <div className="overview-card system-card">
                <h3>GPU | {systemInfo.gpu[0].model}</h3>
                <div className="system-stat">
                  <div className="stat-label">VRAM</div>
                  <div className="stat-value">{systemInfo.gpu[0].vram ? `${systemInfo.gpu[0].vram} MB` : 'N/A'}</div>
                </div>
                <div className="system-details">
                  <span>{systemInfo.gpu[0].vendor}</span>
                </div>
              </div>
            )}

            {systemInfo.disk && systemInfo.disk.length > 0 && (
              <div className="overview-card system-card">
                <h3>Disk | {systemInfo.disk[0].fs}</h3>
                <div className="system-stat">
                  <div className="stat-label">Verwendet</div>
                  <div className="stat-value">{formatBytes(systemInfo.disk[0].used)} / {formatBytes(systemInfo.disk[0].size)}</div>
                  <div className="progress-bar">
                    <div className="progress-fill disk" style={{ width: `${systemInfo.disk[0].usagePercent}%` }}></div>
                  </div>
                </div>
                <div className="system-details">
                  <span>{systemInfo.disk[0].usagePercent.toFixed(1)}% Auslastung</span>
                  <span>{formatBytes(systemInfo.disk[0].available)} frei</span>
                </div>
              </div>
            )}

            <div className="overview-card system-card">
              <h3>System | {systemInfo.os.hostname}</h3>
              <div className="system-stat">
                <div className="stat-label">Betriebssystem</div>
                <div className="stat-value-small">{systemInfo.os.distro}</div>
              </div>
              <div className="system-details">
                <span>{systemInfo.os.platform} {systemInfo.os.arch}</span>
                <span>{systemInfo.processes.running} Prozesse aktiv</span>
              </div>
            </div>

            <div className="overview-card system-card">
              <h3>Netzwerk</h3>
              <div className="system-stat">
                <div className="stat-label">Download</div>
                <div className="stat-value">{formatBytes(systemInfo.network.downloadSpeed)}/s</div>
              </div>
              <div className="system-stat">
                <div className="stat-label">Upload</div>
                <div className="stat-value">{formatBytes(systemInfo.network.uploadSpeed)}/s</div>
              </div>
            </div>
          </>
        )}
        </>
      )}
      </div>
    </div>
  );
}

function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatTime(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

export default Overview;
